import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import { useState, useMemo } from 'react';
import { GradientButton } from '../gradients/GradientButton';
import { GradientTypography } from '../gradients/GradientTypography';
import { CurrencyState } from '../../contexts/CurrencyContext';
import { getFilterOptions } from './filterOptionsMap';
import { Game } from 'functions/src/types/firestore/Game';
import { CatalogueFilter } from './CatalogueFilter';
import { toCamelCase } from '../../util/string';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import { GradientIcon } from '../gradients/GradientIcon';
import { useMobile } from 'src/hooks/useMobile';
import { MIN_HEADER_HEIGHT } from '../header/Header';
import { ImageOptimized } from '../image/ImageOptimized';

type CatalogueFiltersProps = {
  currency: CurrencyState;
  handleFilterChange: (
    name: string,
    filter: {
      values: string[];
      enabled: boolean;
    },
  ) => void;
  parent: 'liked' | 'inventory' | 'game';
  filtersFromQuery: {
    [name: string]: { values: number[]; enabled: boolean };
  };
} & Partial<Pick<Game, 'title'>>;

const STICKY_ELEMENT_OFFSET = '70px';

export function CatalogueFilters({
  currency,
  handleFilterChange,
  parent,
  title,
  filtersFromQuery,
}: CatalogueFiltersProps) {
  const isMobile = useMobile();
  const [isOpen, setIsOpen] = useState(!isMobile);
  const handleToggle = () => {
    setIsOpen((prev) => {
      return !prev;
    });
  };
  const theme = useTheme();
  const { currencyOptions } = currency;
  //parent can be either liked (from profile),inventory(from profile), or game (survivors/weapons/etc tab on game page)
  const filters = useMemo(() => {
    return getFilterOptions({
      parent,
      currencyOptions,
      title: title as string,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent, currencyOptions, title]);
  const getFiltersFromQuery = (filter) => {
    if (!filtersFromQuery) {
      return;
    }
    return filtersFromQuery[`${toCamelCase(filter.title)}`];
  };
  const [cleared, setCleared] = useState(false);
  return isOpen ? (
    <Fade in={isOpen} timeout={{ enter: 200 }}>
      <Box>
        <Box
          sx={{
            position: 'sticky',
            top: STICKY_ELEMENT_OFFSET,
            pb: 1,
            width: isMobile ? '312px' : '400px',
            WebkitMask: 'linear-gradient( -135deg, transparent 27px, red 0)',
            WebkitMaskRepeat: 'no-repeat',
            background: theme.palette.background.elevation[8],
            backdropFilter: theme.glass[2],
            borderLeft: 'none',
            zIndex: 1,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: -1,
              background: theme.palette.primary.vertical,
              clipPath:
                'polygon(0 0,calc(100% - 40.00px) 0,calc(100% - 20px) 20px,100% 40.00px,100% 100%,0 100%,0 0,2px  2px ,2px calc(100% - 2px),calc(100% - 2px) calc(100% - 2px),calc(100% - 2px) calc(40.00px + 0.83px),calc(100% - 20px - 1.41px) calc(20px + 1.41px),calc(100% - 40.00px - 0.83px) 2px,2px 2px)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              pl: 6,
              pr: 4,
              pt: 2,
              pb: 1,
              boxShadow: theme.shadows[7],
            }}
          >
            <GradientTypography
              gradientColor="primary.vertical"
              variant="h5"
              sx={{
                textTransform: 'uppercase',
              }}
            >
              Filters
            </GradientTypography>
            <Box sx={{ '&:hover': { cursor: 'pointer' }, pr: 6, pt: 2 }}>
              <ImageOptimized
                onClick={handleToggle}
                src={'/assets/icons/left-arrow-gradient.svg'}
                width={20}
                height={20}
                alt={'Close catalog filters'}
              />
            </Box>
          </Box>
          <Divider orientation="horizontal" />
          <Box
            sx={{
              'overflow-y': 'scroll',
              maxHeight: `calc(100vh - ${MIN_HEADER_HEIGHT} - ${STICKY_ELEMENT_OFFSET})`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                width: '100%',
                gap: 3,
                mt: 3,
                mb: 4,
                pl: 6,
                pr: 4,
              }}
            >
              <Box sx={{ width: '100%' }}>
                <GradientButton
                  onClick={() => {
                    setCleared(true);
                    setTimeout(() => {
                      setCleared(false);
                    }, 500);
                  }}
                  sx={{ width: '100%' }}
                >
                  Clear Filters
                </GradientButton>
              </Box>
              {/* TODO: Bring back once functionality is determined 
        <Box sx={{ width: '100%' }}>
        <GradientButton sx={{ width: '100%' }}>Advanced</GradientButton>
      </Box> */}
            </Box>
            {/* TODO: may also want to adjust this */}

            {filters &&
              filters.map((filter) => {
                return (
                  <CatalogueFilter
                    filterFromQuery={getFiltersFromQuery(filter)}
                    key={filter.title}
                    handleFilterChange={handleFilterChange}
                    cleared={cleared}
                    filter={filter}
                  />
                );
              })}
          </Box>
        </Box>
      </Box>
    </Fade>
  ) : (
    <Fade in={!isOpen}>
      <Box
        sx={{
          position: 'sticky',
          top: STICKY_ELEMENT_OFFSET,
          background: theme.palette.background.elevation[10],
          backdropFilter: 'blur(25px)',
          px: 2,
          pt: 2,
          mb: 60,
          height: '50px',
          backgroundClip: 'padding-box',
          '-webkitMaskRepeat': 'no-repeat',
          border: '2px solid #279BF6',
          borderRadius: '0px 10px 10px 0px',
          borderLeft: 'none',
        }}
      >
        <Box
          onClick={handleToggle}
          sx={{
            '&:hover': { cursor: 'pointer' },
            width: 30,
            height: 50,
          }}
        >
          <GradientIcon
            gradientColor="primary.verticalInverse"
            IconComponent={FilterAltRoundedIcon}
            sx={{ width: 30, height: 30 }}
          />
        </Box>
      </Box>
    </Fade>
  );
}
